import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormControlLabel, FormGroup, Switch } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import type { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import DonutChart, {
  type DonutChartDataTypes
} from '@/components/charts/DonutChart';
import ExportButtonV2 from '@/components/ExportButtonV2';
import { triggerFileDownload } from '@/components/ExportButtonV2/ExportButtonV2.utils';
import GenericChartWidget from '@/components/GenericChartWidget';
import { useQBFilterActions } from '@/components/QueryBuilder/QueryBuilder.hooks';
import { VMP_VULNERABILITY_COLORS } from '@/constants/chartcolors.constants';
import { VULNERABILITIES_FILTERS_STORAGE_KEY } from '@/constants/common';
import { VulnerabilitiesTable } from '@/features/VulnerabilitiesTable';
import { useHandleError } from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations/Organizations.store';
import { mapFiltersToRequestParams } from '@/pages/Tickets/Tickets.store';

import {
  clearVulnerabilitiesSearchCache,
  useFetchVMPEExport,
  vmpWidgetsCollapsedAtom,
  vulnerabilitiesGroupsAtom
} from '../VulnerabilityManagement.store';
import useStyles from '../VulnerabilityManagement.styles';
import VulnerabilityManagementSearch from '../VulnerabilityManagementSearch';
import {
  isGroupedByVulnerabilityAtom,
  vulnerabilitiesFiltersAtom,
  vulnerabilitiesGridApiAtom,
  vulnerabilitiesTimeFilterAtom
} from './VMPVulnerabilities.store';

const VMPVulnerabilities = () => {
  const vulnerabilitiesFilters = useAtomValue(vulnerabilitiesFiltersAtom);
  const vulnerabilitiesTimeFilter = useAtomValue(vulnerabilitiesTimeFilterAtom);
  const vulnerabilitiesGroups = useAtomValue(vulnerabilitiesGroupsAtom);
  const widgetsCollapsed = useAtomValue(vmpWidgetsCollapsedAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const vulnerabilitiesGridApi = useAtomValue(vulnerabilitiesGridApiAtom);
  const [isReady, setIsReady] = useState(false);
  const [isGroupedByVulnerability, setIsGroupedByVulnerability] = useAtom(
    isGroupedByVulnerabilityAtom
  );

  const { classes } = useStyles({ widgetsCollapsed });
  const { updateFilters, removeFilter } = useQBFilterActions(
    `${VULNERABILITIES_FILTERS_STORAGE_KEY}V2`
  );

  const { mutateAsync: fetchVMPEExport } = useFetchVMPEExport();
  const handleError = useHandleError();

  // ---- Cvss level donut data
  const vulnerabilitiesByCvssLevelData = useMemo(() => {
    return (vulnerabilitiesGroups?.cvss_level || []).map((group) => {
      return {
        name: group.label,
        count: group.count,
        color:
          VMP_VULNERABILITY_COLORS[
            group.label.toUpperCase() as keyof typeof VMP_VULNERABILITY_COLORS
          ]
      };
    });
  }, [vulnerabilitiesGroups]) as DonutChartDataTypes[];
  // --------

  // ---- Pondurance score donut data
  const vulnerabilitiesByScoreLevelData = useMemo(() => {
    return (vulnerabilitiesGroups?.pondurance_score || []).map((group) => {
      return {
        name: group.label,
        count: group.count,
        color:
          VMP_VULNERABILITY_COLORS[
            group.label.toUpperCase() as keyof typeof VMP_VULNERABILITY_COLORS
          ]
      };
    });
  }, [vulnerabilitiesGroups]) as DonutChartDataTypes[];
  // --------

  // ---- Scanner type donut data
  const vulnerabilitiesByScannerTypeData = useMemo(() => {
    return (vulnerabilitiesGroups?.scanner_type || []).map(
      (group, index: number) => {
        return {
          name: group.label,
          count: group.count,
          color: `var(--mui-palette-chartColors-${index})`
        };
      }
    );
  }, [vulnerabilitiesGroups]) as DonutChartDataTypes[];
  // --------

  // -----------------------------------------------------------------
  const handleChartClick = (value: string, key: string) => {
    const filterExists = (vulnerabilitiesFilters || []).some(
      (filter) =>
        filter.field === key &&
        ((typeof filter.value === 'string' && filter.value === value) ||
          (Array.isArray(filter.value) && filter.value.includes(value)))
    );

    if (filterExists) {
      removeFilter(key);
      return;
    }

    updateFilters([{ field: key, operator: '__eq', value }]);
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const mappedFilters = mapFiltersToRequestParams(
        vulnerabilitiesFilters || [],
        vulnerabilitiesTimeFilter,
        'date_identified',
        'last_changed_at'
      );

      if (selectedOrganization.id > 0) {
        mappedFilters.organization__label = selectedOrganization?.label || '';
      }

      const response = await fetchVMPEExport({
        format,
        vmpeFilters: mappedFilters,
        isGroupedByVulnerability
      });

      const mimeType =
        format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json';
      const blob = new Blob([response.data], { type: mimeType });
      triggerFileDownload(blob, format, 'vulnerabilities-export');
    } catch (error) {
      handleError(error as AxiosError, 'Failure to export vulnerabilities.');
    }
  };

  const refetch = () => {
    clearVulnerabilitiesSearchCache();
    vulnerabilitiesGridApi?.refreshServerSide({ purge: true });
  };

  return (
    <div className={classes.vmpPage}>
      <div className={classes.vmpPageContent}>
        <div className={classes.vmpTable}>
          <Box className={classes.vmpTableWrapperBox}>
            <Paper className={classes.vmpTablePaper} elevation={2}>
              <Box className={classes.vmpTableSearchBox}>
                <VulnerabilityManagementSearch onFiltersReady={setIsReady} />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ maxHeight: '30px', ml: 2 }}
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isGroupedByVulnerability}
                          onClick={() => {
                            setIsGroupedByVulnerability(
                              !isGroupedByVulnerability
                            );
                          }}
                        />
                      }
                      label="Group by CVE"
                    />
                  </FormGroup>
                  <ExportButtonV2 handleClick={handleExport} json csv />
                  <Button onClick={() => refetch()} variant="outlined">
                    <FontAwesomeIcon size="lg" icon={faRefresh} />
                  </Button>
                </Stack>
              </Box>
              {isReady && (
                <VulnerabilitiesTable tableLocation="vulnerabilities-tab" />
              )}
            </Paper>
          </Box>
        </div>
        {!widgetsCollapsed && (
          <>
            <div className={classes.vmpWidget}>
              <GenericChartWidget title="Vulnerabilities by Score">
                <DonutChart
                  data={vulnerabilitiesByScoreLevelData}
                  onChartClick={(value) => {
                    handleChartClick(value.datum.name, 'pondurance_score');
                  }}
                />
              </GenericChartWidget>
            </div>
            <div className={classes.vmpWidget}>
              <GenericChartWidget title="Vulnerabilities by CVSS">
                <DonutChart
                  data={vulnerabilitiesByCvssLevelData}
                  onChartClick={(value) => {
                    handleChartClick(value.datum.name, 'cvss_level');
                  }}
                />
              </GenericChartWidget>
            </div>
            <div className={classes.vmpWidget}>
              <GenericChartWidget title="Vulnerabilities by Scanner Type">
                <DonutChart
                  data={vulnerabilitiesByScannerTypeData}
                  onChartClick={(value) => {
                    handleChartClick(value.datum.name, 'scanner_type');
                  }}
                  resolveCssVariables
                />
              </GenericChartWidget>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VMPVulnerabilities;
